import { Component, Input, ViewChild, OnInit, OnChanges } from '@angular/core';
import { WorkflowApproverView } from 'app/models/form/WorkflowApprover';
import { WorkflowStepView } from 'app/models/form/WorkflowStepView';
import { Popover } from 'primeng/popover';

@Component({
	selector: 'client-stepper-workflow',
	standalone: false,
	template: `
		<div *ngIf="workflowSteps?.length > 0" class="mb-4">
			<p-stepper [value]="currentStepValue">
				<p-step-list>
					<p-step
						*ngFor="let step of workflowSteps; let i = index; let last = last"
						[value]="i + 1"
						class="flex flex-row gap-0 flex-auto"
						[style]="{ gap: '0' }"
					>
						<ng-template #content let-activateCallback="activateCallback" let-value="value">
							<div class="step-container" (click)="showApprovers($event, step)">
								<p-button
									[icon]="getStepIcon(step)"
									[rounded]="true"
									[severity]="getButtonSeverity(step, i)"
									[label]="getStepLabel(step, i)"
									size="small"
									[style]="{ width: '2rem', height: '2rem', padding: '0' }"
								></p-button>
								<span class="step-label">{{ step.name }}</span>
							</div>
							<p-stepper-separator *ngIf="!last"></p-stepper-separator>
						</ng-template>
					</p-step>
				</p-step-list>
			</p-stepper>

			<p-popover [appendTo]="'body'" position="bottom" #popover>
				<div class="card shadow-sm" style="min-width: 18rem">
					<div
						class="card-header py-2 px-3"
						[style.background-color]="isDeniedStep(selectedStep) ? '#dc3545' : '#c3a76a'"
						[style.color]="isDeniedStep(selectedStep) ? 'white' : 'inherit'"
					>
						<strong>{{ getPopoverTitle() }}</strong>
					</div>
					<div class="card-body py-0">
						<div *ngIf="selectedApprovers?.length; else noApprovers">
							<div *ngFor="let approver of selectedApprovers" class="py-3" style="border-bottom: 1px solid lightgray;">
								<div class="flex-grow-1">
									<div class="d-flex align-items-center justify-content-between">
										<div class="fw-bold">{{ approver.name }}</div>
										<p-tag
											*ngIf="approver.hasApproved"
											[value]="getApprovalAndTagText()"
											severity="success"
											[icon]="'pi pi-check'"
											[style]="{ 'font-size': '0.75rem' }"
										></p-tag>
										<p-tag
											*ngIf="isDeniedStep(selectedStep) && !approver.hasApproved"
											value="Denied"
											severity="danger"
											[icon]="'pi pi-times'"
											[style]="{ 'font-size': '0.75rem' }"
										></p-tag>
									</div>
									<small class="text-muted">Group: {{ approver.groupName ? approver.groupName : 'n/a' }}</small>

									<div *ngIf="approver.hasApproved && approver.approvedDate" class="mt-2">
										<small class="text-success">
											{{ getApprovalAndTagText() }} {{ approver.approvedDate | date : 'short' }}
										</small>
										<div *ngIf="approver.approvedBy && approver.approvedBy !== approver.name">
											<small class="text-muted">by {{ approver.approvedBy }}</small>
										</div>
									</div>
								</div>
							</div>
						</div>
						<ng-template #noApprovers>
							<div *ngIf="isDeniedStep(selectedStep)" class="py-3">
								<p-tag
									value="Denied"
									severity="danger"
									[icon]="'pi pi-times'"
									[style]="{ 'font-size': '0.75rem' }"
								></p-tag>
							</div>
							<div *ngIf="!isDeniedStep(selectedStep)" class="text-muted text-center py-3">
								No approvers for this step.
							</div>
						</ng-template>
					</div>
				</div>
			</p-popover>
		</div>
	`,

	styles: [
		`
			.step-container {
				display: flex;
				flex-direction: column;
				align-items: center;
				gap: 8px;
				cursor: pointer;
			}

			.step-label {
				font-size: 0.75rem;
				text-align: center;
				max-width: 80px;
				line-height: 1.2;
				word-wrap: break-word;
			}

			::ng-deep .p-popover:after {
				left: calc(var(--p-popover-arrow-offset) + var(--overlayArrowLeft) - 5px) !important;
			}

			::ng-deep .p-popover:before {
				left: calc(var(--p-popover-arrow-offset) + var(--overlayArrowLeft) - 5px) !important;
			}
		`,
	],
})
export class StepperWorkflowComponent implements OnInit, OnChanges {
	@ViewChild('popover') popover: Popover;
	@Input({ required: true }) set workflowSteps(value: WorkflowStepView[] | undefined) {
		this._workflowSteps = value ?? [];
	}
	get workflowSteps(): WorkflowStepView[] {
		return this._workflowSteps;
	}

	@Input({ required: true }) set allApprovers(value: WorkflowApproverView[] | undefined) {
		this._allApprovers = value ?? [];
	}
	get allApprovers(): WorkflowApproverView[] {
		return this._allApprovers;
	}

	@Input() fulfillmentApproverName?: string;

	private _workflowSteps: WorkflowStepView[] = [];
	private _allApprovers: WorkflowApproverView[] = [];

	selectedApprovers: WorkflowApproverView[] = [];
	selectedStep: WorkflowStepView | null = null; // Track which step was clicked
	currentStepValue = 1;

	ngOnInit() {
		this.setCurrentStep();
	}

	ngOnChanges() {
		this.setCurrentStep();
	}

	private setCurrentStep() {
		if (this.workflowSteps?.length) {
			const currentStepIndex = this.workflowSteps.findIndex((step) => step.isCurrent);
			if (currentStepIndex >= 0) {
				const currentStep = this.workflowSteps[currentStepIndex];
				if (this.isDeniedStep(currentStep) && currentStepIndex > 0) {
					this.currentStepValue = currentStepIndex;
				} else {
					this.currentStepValue = currentStepIndex + 1;
				}
			} else {
				const incompleteIndex = this.workflowSteps.findIndex((step) => !step.isCompleted);
				this.currentStepValue = incompleteIndex >= 0 ? incompleteIndex + 1 : this.workflowSteps.length;
			}
		}
	}

	getButtonSeverity(step: WorkflowStepView, index: number): 'success' | 'secondary' | 'danger' {
		if (this.isDeniedStep(step)) {
			return 'danger';
		}

		if (step.isCompleted) {
			return 'success';
		}

		if (this.isCurrentStep(index)) {
			return 'success';
		}

		return 'secondary';
	}

	isDeniedStep(step: WorkflowStepView | null | undefined): boolean {
		return step?.name === 'Denied';
	}

	getStepIcon(step: WorkflowStepView): string | undefined {
		if (this.isDeniedStep(step)) {
			return 'pi pi-times';
		}
		return step.isCompleted ? 'pi pi-check' : undefined;
	}

	getStepLabel(step: WorkflowStepView, index: number): string {
		if (this.isDeniedStep(step) || step.isCompleted) {
			return '';
		}
		return (index + 1).toString();
	}

	private isCurrentStep(index: number): boolean {
		return index + 1 === this.currentStepValue;
	}

	showApprovers(event: MouseEvent, step: WorkflowStepView) {
		this.selectedStep = step;

		if (this.fulfillmentApproverName && this.isFulfillmentStep(step)) {
			this.selectedApprovers = [
				{
					userId: '',
					name: this.fulfillmentApproverName,
					hasApproved: step.isCompleted || false,
					groupName: '',
					workflowStepId: step.id,
					approvedDate: undefined, // not recording this in the system yet
					approvedBy: step.isCompleted ? this.fulfillmentApproverName : undefined,
					approvedById: '',
					oldUserIdName: '',
					oldUserName: '',
					workflowStep: 'Fulfillment',
					isThresholdUser: false,
				},
			];
		} else {
			this.selectedApprovers = this.allApprovers.filter((approver) => approver.workflowStepId === step.id);
		}

		if (this.popover.overlayVisible) {
			this.popover.hide();
			setTimeout(() => {
				this.popover.show(event);
			}, 200); // fails to reopen without delay
		} else {
			this.popover.show(event);
		}
	}

	isFulfillmentStep(step: WorkflowStepView | null | undefined): boolean {
		return !!(this.fulfillmentApproverName && step?.name?.toLowerCase().includes('fulfillment'));
	}

	getPopoverTitle(): string {
		if (this.isDeniedStep(this.selectedStep)) return 'Denied';
		if (!this.fulfillmentApproverName) return 'Approvers';

		return this.isFulfillmentStep(this.selectedStep) ? 'Fulfillment' : 'Approvers';
	}

	getApprovalAndTagText(): string {
		if (!this.fulfillmentApproverName) return 'Approved';

		return this.isFulfillmentStep(this.selectedStep) ? 'Fulfilled' : 'Approved';
	}
}
